import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Activity,
  ArrowUpRight,
  BarChart3,
  Bell,
  CheckCircle2,
  Database,
  Download,
  ExternalLink,
  FileSpreadsheet,
  Hash,
  Heart,
  Image as ImageIcon,
  KeyRound,
  Layers,
  MessageCircle,
  Radio,
  RefreshCw,
  Search,
  Server,
  ShieldCheck,
  TrendingUp,
  Users,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useWebSocket } from '../hooks/useWebSocket';
import { getAvatarFallbackUrl, getMediaUrl, getProfilePictureUrl } from '../utils/mediaProxy';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const API_KEY_STORAGE_KEY = 'instagram_public_api_key';
const ADMIN_API_KEY_STORAGE_KEY = 'instagram_admin_api_key';
const WS_URL =
  process.env.REACT_APP_WS_URL ||
  `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws`;

interface Profile {
  id: number;
  username: string;
  profile_name: string;
  followers_count: number;
  following_count: number;
  posts_count: number;
  bio: string;
  profile_pic_url: string;
  is_verified: number;
  is_private: number;
  last_updated: string;
}

interface RecentPost {
  id: string;
  shortcode: string;
  url: string;
  caption?: string | null;
  likes_count: number;
  comments_count: number;
  views_count: number;
  taken_at?: string | null;
  media_type: string;
  is_video: boolean;
  thumbnail_url?: string | null;
  display_url?: string | null;
  hashtags: string[];
  mentions: string[];
  engagement_rate: number;
}

interface ProfileInsights {
  profile: Profile;
  analytics: {
    follower_following_ratio?: number | null;
    average_likes: number;
    average_comments: number;
    average_views: number;
    average_engagement_rate: number;
    recent_posts_analyzed: number;
    posting_frequency_per_week: number;
    media_mix: Record<string, number>;
    top_hashtags: string[];
    top_mentions: string[];
    highlights_count: number;
    reels_count: number;
    is_business_account: boolean;
    category_name?: string | null;
    external_url?: string | null;
    average_interactions: number;
  };
  recent_posts: RecentPost[];
  external_links: Array<{
    source_surface: string;
    raw_url: string;
    expanded_url?: string | null;
    final_url?: string | null;
    domain?: string | null;
    http_status?: number | null;
    is_linktree: boolean;
  }>;
  aggregates: Array<{
    window_label: string;
    posts_total: number;
    reels_total: number;
    images_total: number;
    likes_total: number;
    comments_total: number;
    views_total: number;
    avg_posts_per_week: number;
  }>;
  content_intelligence?: {
    posting_velocity_label: string;
    best_media_type?: string | null;
    best_posting_weekday?: string | null;
    posting_consistency_score: number;
    recommendations: string[];
  };
  watchlist_benchmark?: {
    tracked_profiles: number;
    followers_percentile: number;
    engagement_percentile: number;
    similar_profiles: Array<{
      username: string;
      profile_name?: string | null;
      followers_count: number;
      average_engagement_rate: number;
      shared_hashtags: string[];
    }>;
    message: string;
  };
}

interface MediaAsset {
  post_id: string;
  shortcode: string;
  post_url: string;
  media_type: string;
  asset_type: string;
  asset_url: string;
  preview_url?: string | null;
  video_url?: string | null;
  caption?: string | null;
  order_index: number;
  owner_username?: string | null;
}

interface PublicPostInspection {
  source_url: string;
  canonical_url: string;
  shortcode: string;
  media_type: string;
  is_video: boolean;
  caption?: string | null;
  likes_count: number;
  comments_count: number;
  views_count: number;
  taken_at?: string | null;
  thumbnail_url?: string | null;
  display_url?: string | null;
  video_url?: string | null;
  hashtags: string[];
  mentions: string[];
  coauthor_usernames: string[];
  owner: {
    username: string;
    full_name?: string | null;
    profile_pic_url?: string | null;
    is_verified: boolean;
    profile_url?: string | null;
  };
  assets: MediaAsset[];
  source: string;
  public_comments_message: string;
}

interface ProfileMediaExtraction {
  username: string;
  extracted_at: string;
  source: string;
  message: string;
  post_count: number;
  asset_count: number;
  assets: MediaAsset[];
}

interface HashtagExplorer {
  hashtag: string;
  canonical_url: string;
  source: string;
  browser_used: boolean;
  blocked_by_instagram: boolean;
  message: string;
  extracted_post_count: number;
  items: PublicPostInspection[];
}

interface ScrapeResponse {
  success_count: number;
  failed_count: number;
  results: Array<{ username: string; action: string; error?: string; recent_posts?: number }>;
  message?: string | null;
  job_id?: string | null;
  status_url?: string | null;
}

interface ProxyRotationStatus {
  enabled: boolean;
  configured_count: number;
  available_count: number;
  optimistic_available_count?: number;
  verified_live_count?: number;
  verified_available_count?: number;
  dead_count?: number;
  checked_count?: number;
  unchecked_count?: number;
  checking_count?: number;
  health_check_enabled?: boolean;
  health_scan_running?: boolean;
  health_age_seconds?: number | null;
  cooldown_count: number;
  max_attempts: number;
  cooldown_seconds: number;
  retry_same_url: boolean;
}

interface CapabilityStatus {
  status: string;
  capabilities: string[];
  rate_limits?: Record<string, string | number>;
  proxy_rotation?: ProxyRotationStatus;
}

interface AdminDeployStatus {
  enabled: boolean;
  running: boolean;
  active_pid?: number | null;
  output: string;
  error_output: string;
  restart_output: string;
  restart_error_output: string;
  watchdog_output: string;
  watchdog_checked_at?: string | null;
  watchdog_modified_at?: string | null;
}

interface AdminTrackingRefreshSummary {
  lock_acquired: boolean;
  selected_profiles: number;
  checked_profiles: number;
  failed_profiles: number;
  skipped_non_premium: number;
  events_observed: number;
  events_detected: number;
  events_filtered: number;
  pushes_sent: number;
  pushes_deferred: number;
  pushes_failed: number;
  invalid_tokens: number;
  duration_seconds: number;
  profiles: AdminTrackingProfileDiagnostic[];
}

interface SafeHikerResponseDiagnostic {
  response_type?: string;
  response_keys?: string[];
  response_key_paths?: string[];
  response_length?: number;
  response_item_types?: string[];
  next_max_id_present?: boolean;
  next_max_id?: unknown;
  resolved_cursor_key?: string | null;
  resolved_cursor?: unknown;
  has_more_present?: boolean;
  has_more?: unknown;
  resolved_has_more_key?: string | null;
  resolved_has_more?: unknown;
  tuple_cursor_candidate_present?: boolean;
  tuple_cursor_candidate_path?: string | null;
  tuple_cursor_candidate?: unknown;
  status_fields?: Record<string, unknown>;
  error_fields?: Record<string, unknown>;
}

interface HikerRelationshipPageDiagnostic extends SafeHikerResponseDiagnostic {
  page?: number;
  status?: number;
  items?: number;
  elapsed_seconds?: number;
  request_cursor_present?: boolean;
  request_cursor?: string | null;
}

interface AdminTrackingProfileDiagnostic {
  username: string;
  status: string;
  reason: string;
  expected_event_type?: string | null;
  retry_seconds?: number;
  checked_at?: string;
  next_retry_at?: string;
  consecutive_failures?: number;
  error?: {
    type?: string;
    message?: string;
    status_code?: number;
    endpoint?: string;
    upstream?: SafeHikerResponseDiagnostic;
    cause?: {
      type?: string;
      message?: string;
    };
  };
  incomplete_relationship?: {
    relationship?: string;
    expected_count?: number;
    loaded_count?: number;
    is_private?: boolean | null;
    profile?: {
      endpoint?: string;
      status?: number;
      elapsed_seconds?: number;
      response?: SafeHikerResponseDiagnostic;
    };
    upstream?: {
      endpoint?: string;
      stop_reason?: string;
      expected_count?: number;
      loaded_count?: number;
      pagination_complete?: boolean;
      count_complete?: boolean;
      pages?: HikerRelationshipPageDiagnostic[];
      [key: string]: unknown;
    };
  };
  baseline_exists?: boolean;
  source?: {
    host?: string;
    http_cache_bypass?: boolean;
    profile_status?: number;
    profile_elapsed_seconds?: number;
  };
  previous?: Record<string, number>;
  current?: Record<string, number | boolean>;
  changes?: Record<string, string[]>;
  event_types_observed?: string[];
  event_types_selected?: string[];
  event_types_persisted?: string[];
  delivery?: {
    sent: number;
    deferred: number;
    failed: number;
    invalid_tokens: number;
  };
}

interface AdminTrackingTestPushResult {
  event_type: string;
  registered_active_tokens: number;
  notifications_enabled_tokens: number;
  tracking_ready_tokens: number;
  selected_tokens: number;
  sent: number;
  failed: number;
  invalid_tokens: number;
  apns_configured: boolean;
  results: Array<{
    installation: string;
    username: string;
    environment: string;
    environment_configured: boolean;
    status_code: number;
    reason?: string | null;
    apns_id?: string | null;
    succeeded: boolean;
  }>;
}

interface AdminTrackingRefreshStatus {
  running: boolean;
  event_type?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  summary?: AdminTrackingRefreshSummary | null;
  error?: string | null;
  message?: string;
}

const TRACKING_DEBUG_EVENT_OPTIONS = [
  { value: '', label: 'All detected changes' },
  { value: 'following_removed', label: 'I unfollowed someone' },
  { value: 'follower_removed', label: 'Someone unfollowed me' },
  { value: 'follower_added', label: 'New follower' },
  { value: 'following_added', label: 'I followed someone' },
  { value: 'possible_block', label: 'Possible block' },
  { value: 'new_post', label: 'New post' },
  { value: 'new_reel', label: 'New reel' },
  { value: 'new_story', label: 'New story' },
  { value: 'likes_increased', label: 'New likes' },
  { value: 'comments_increased', label: 'New comments' },
  { value: 'profile_bio_changed', label: 'Bio changed' },
  { value: 'profile_name_changed', label: 'Profile name changed' },
  { value: 'profile_avatar_changed', label: 'Avatar changed' },
  { value: 'profile_privacy_changed', label: 'Privacy changed' },
  { value: 'profile_verification_changed', label: 'Verification changed' },
  { value: 'account_unavailable', label: 'Account unavailable' },
] as const;

const trackingDebugEventLabel = (eventType?: string | null): string => (
  TRACKING_DEBUG_EVENT_OPTIONS.find((option) => option.value === (eventType || ''))?.label
  || eventType
  || 'All detected changes'
);

const getStoredApiKey = () => {
  const envKey = process.env.REACT_APP_PUBLIC_API_KEY || '';
  if (typeof window === 'undefined') return envKey;
  return window.localStorage.getItem(API_KEY_STORAGE_KEY) || envKey;
};

const getStoredAdminApiKey = () => {
  if (typeof window === 'undefined') return '';
  return window.localStorage.getItem(ADMIN_API_KEY_STORAGE_KEY) || '';
};

const buildAuthHeaders = (apiKey: string, headers?: HeadersInit): Headers => {
  const nextHeaders = new Headers(headers);
  nextHeaders.set('ngrok-skip-browser-warning', '1');
  const trimmedKey = apiKey.trim();
  if (trimmedKey) nextHeaders.set('X-API-Key', trimmedKey);
  return nextHeaders;
};

const buildAdminHeaders = (apiKey: string, adminKey: string, headers?: HeadersInit): Headers => {
  const nextHeaders = buildAuthHeaders(apiKey, headers);
  const trimmedAdminKey = adminKey.trim();
  if (trimmedAdminKey) nextHeaders.set('X-Admin-Key', trimmedAdminKey);
  return nextHeaders;
};

const parseDownloadFilename = (contentDisposition: string | null, fallback: string) => {
  if (!contentDisposition) return fallback;
  const utfMatch = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utfMatch?.[1]) return decodeURIComponent(utfMatch[1].replace(/"/g, ''));
  const regularMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return regularMatch?.[1] || fallback;
};

const extractApiErrorMessage = (data: any, fallback: string) => {
  const detail = data?.detail ?? data?.message;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    const retry = detail.retry_after_seconds ? ` Retry in ~${detail.retry_after_seconds}s.` : '';
    return `${detail.message || fallback}${retry}`;
  }
  return fallback;
};

const fetchJson = async <T,>(url: string, options: RequestInit = {}, apiKey = ''): Promise<T> => {
  const response = await fetch(url, {
    ...options,
    headers: buildAuthHeaders(apiKey, options.headers),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(extractApiErrorMessage(data, `Request failed with ${response.status}`));
  }
  return data as T;
};

const formatNumber = (value: number | undefined | null): string => {
  const num = Number(value || 0);
  if (num >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(num);
};

const proxyHealthClass = (status?: ProxyRotationStatus) => {
  if (!status?.enabled || !status.configured_count) return 'text-[#8b9bb3]';
  const liveCount = status.verified_available_count ?? status.verified_live_count ?? status.available_count;
  const ratio = liveCount / status.configured_count;
  if (ratio >= 0.75) return 'text-[#8fffb1]';
  if (ratio >= 0.25) return 'text-[#ffd36b]';
  return 'text-[#ff9aa8]';
};

const proxyLiveCount = (status?: ProxyRotationStatus) =>
  status?.verified_live_count ?? status?.verified_available_count ?? status?.available_count ?? 0;

const proxyCheckedCount = (status?: ProxyRotationStatus) =>
  status?.checked_count ?? (status?.health_check_enabled ? 0 : status?.configured_count ?? 0);

const safeUsername = (value: string) => value.trim().replace(/^@/, '');

const diagnosticValue = (value: unknown) => {
  if (value === undefined) return 'unknown';
  if (value === null) return 'null';
  if (typeof value === 'string') return value || 'empty';
  return JSON.stringify(value);
};

const diagnosticFields = (value?: Record<string, unknown>) =>
  value && Object.keys(value).length > 0 ? JSON.stringify(value) : 'none';

const MetricCard: React.FC<{
  label: string;
  value: string;
  detail?: string;
  icon: React.ReactNode;
}> = ({ label, value, detail, icon }) => (
  <div className="rounded-xl border border-[#243447]/60 bg-[#111827]/80 p-4">
    <div className="mb-3 flex items-center justify-between gap-3">
      <div className="text-xs uppercase tracking-[0.18em] text-[#8b9bb3]">{label}</div>
      <div className="text-[#8fefff]">{icon}</div>
    </div>
    <div className="text-2xl font-bold text-white">{value}</div>
    {detail ? <div className="mt-1 text-xs text-[#8b9bb3]">{detail}</div> : null}
  </div>
);

const TrackingProfileDiagnosticCard: React.FC<{
  profile: AdminTrackingProfileDiagnostic;
}> = ({ profile }) => {
  const incomplete = profile.incomplete_relationship;
  const upstream = incomplete?.upstream;

  return (
    <div className="rounded-lg border border-[#243447]/80 bg-[#080d14]/80 p-2.5">
      <div className="flex items-center justify-between gap-3">
        <span className="font-semibold text-white">@{profile.username}</span>
        <span className={profile.status === 'checked' ? 'text-[#8fffb1]' : 'text-[#ff9aa8]'}>
          {profile.status}
        </span>
      </div>
      <div className="mt-1 text-[#b9c4d4]">{profile.reason}</div>
      {profile.error ? (
        <div className="mt-2 rounded-md border border-[#ff6b6b]/30 bg-[#ff6b6b]/10 p-2 text-[#ffb3bd]">
          <div className="font-semibold">
            {profile.error.type || 'Error'}{profile.error.status_code ? ` · HTTP ${profile.error.status_code}` : ''}
          </div>
          <div className="mt-1 whitespace-pre-wrap break-words font-mono text-[11px]">
            {profile.error.message || 'No error message was returned.'}
          </div>
          {profile.error.endpoint ? (
            <div className="mt-1 font-mono text-[11px] text-[#d8c797]">
              Endpoint: {profile.error.endpoint}
            </div>
          ) : null}
          {profile.error.upstream ? (
            <div className="mt-1 space-y-0.5 break-words font-mono text-[11px] text-[#b9c4d4]">
              <div>Response type: {profile.error.upstream.response_type || 'unknown'}</div>
              <div>Response keys: {profile.error.upstream.response_keys?.join(', ') || 'none'}</div>
              <div>Status fields: {diagnosticFields(profile.error.upstream.status_fields)}</div>
              <div>Error fields: {diagnosticFields(profile.error.upstream.error_fields)}</div>
            </div>
          ) : null}
          {profile.error.cause ? (
            <div className="mt-1 whitespace-pre-wrap break-words text-[11px] text-[#ff9aa8]">
              Caused by {profile.error.cause.type || 'unknown'}: {profile.error.cause.message || 'no message'}
            </div>
          ) : null}
        </div>
      ) : null}
      {incomplete ? (
        <div className="mt-2 rounded-md border border-[#ffd36b]/30 bg-[#ffd36b]/5 p-2 text-[#d8c797]">
          <div className="font-semibold text-[#ffd36b]">
            Incomplete {incomplete.relationship || 'relationship'}: {incomplete.loaded_count ?? 'unknown'}/{incomplete.expected_count ?? 'unknown'}
          </div>
          <div className="mt-1">
            Profile private: {incomplete.is_private === null || incomplete.is_private === undefined ? 'unknown' : incomplete.is_private ? 'yes' : 'no'}
            {incomplete.profile?.endpoint ? ` · profile endpoint ${incomplete.profile.endpoint}` : ''}
            {incomplete.profile?.status ? ` · HTTP ${incomplete.profile.status}` : ''}
          </div>
          <div className="mt-1 font-mono text-[11px] text-[#b9c4d4]">
            Relationship endpoint: {upstream?.endpoint || 'unknown'}
          </div>
          <div className="mt-1">
            Stop: {upstream?.stop_reason || 'unknown'} · pagination complete {upstream?.pagination_complete === undefined ? 'unknown' : upstream.pagination_complete ? 'yes' : 'no'} · count complete {upstream?.count_complete === undefined ? 'unknown' : upstream.count_complete ? 'yes' : 'no'}
          </div>
          {upstream?.pages?.length ? (
            <div className="mt-2 max-h-72 space-y-2 overflow-auto font-mono text-[11px] text-[#b9c4d4]">
              {upstream.pages.map((page, index) => (
                <div className="rounded border border-[#243447]/60 p-1.5" key={`${page.page ?? index}-${index}`}>
                  <div>
                    page {page.page ?? index + 1} · HTTP {page.status ?? 'unknown'} · items {page.items ?? 'unknown'} · {page.elapsed_seconds ?? 'unknown'}s
                  </div>
                  <div>
                    request cursor: {page.request_cursor_present ? diagnosticValue(page.request_cursor) : 'absent'}
                  </div>
                  <div>
                    next_max_id: {page.next_max_id_present ? diagnosticValue(page.next_max_id) : 'absent'} · has_more: {page.has_more_present ? diagnosticValue(page.has_more) : 'absent'}
                  </div>
                  <div>
                    resolved cursor: {page.resolved_cursor_key || 'none'} = {diagnosticValue(page.resolved_cursor)} · resolved has_more: {page.resolved_has_more_key || 'none'} = {diagnosticValue(page.resolved_has_more)}
                  </div>
                  {page.tuple_cursor_candidate_present ? (
                    <div className="text-[#ffd36b]">
                      tuple cursor candidate: {page.tuple_cursor_candidate_path || 'unknown'} = {diagnosticValue(page.tuple_cursor_candidate)}
                    </div>
                  ) : null}
                  <div>
                    response: {page.response_type || 'unknown'}{page.response_length !== undefined ? ` (${page.response_length} items)` : ''} · item types {page.response_item_types?.join(', ') || 'none'}
                  </div>
                  <div className="break-words">
                    keys: {page.response_keys?.join(', ') || 'none'}
                  </div>
                  <div className="break-words">
                    key paths: {page.response_key_paths?.join(', ') || 'none'}
                  </div>
                  <div className="break-words">status fields: {diagnosticFields(page.status_fields)}</div>
                  <div className="break-words">error fields: {diagnosticFields(page.error_fields)}</div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      {profile.source ? (
        <div className="mt-1 text-[#6f8199]">
          Source: {profile.source.host || 'unknown'} · HTTP cache bypass {profile.source.http_cache_bypass ? 'on' : 'off'}
        </div>
      ) : null}
      {profile.previous && profile.current ? (
        <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[#8b9bb3]">
          <span>Following list: {profile.previous.following_list} → {profile.current.following_list}</span>
          <span>Profile count: {profile.previous.following_profile_count} → {profile.current.following_profile_count}</span>
          <span>Followers list: {profile.previous.followers_list} → {profile.current.followers_list}</span>
          <span>Complete: {profile.current.following_complete ? 'yes' : 'no'}</span>
        </div>
      ) : null}
      {profile.changes && Object.values(profile.changes).some((items) => items.length > 0) ? (
        <div className="mt-2 space-y-1 text-[#b9c4d4]">
          {Object.entries(profile.changes).map(([label, items]) => items.length ? (
            <div key={label}>
              <span className="text-[#8b9bb3]">{label.replace(/_/g, ' ')}:</span> {items.join(', ')}
            </div>
          ) : null)}
        </div>
      ) : null}
      <div className="mt-2 text-[#6f8199]">
        Observed: {profile.event_types_observed?.join(', ') || 'none'} · Saved: {profile.event_types_persisted?.join(', ') || 'none'}
      </div>
      {profile.delivery ? (
        <div className="mt-1 text-[#6f8199]">
          APNs: sent {profile.delivery.sent}, deferred {profile.delivery.deferred}, failed {profile.delivery.failed}, invalid {profile.delivery.invalid_tokens}
        </div>
      ) : null}
      {profile.retry_seconds !== undefined ? (
        <div className="mt-1 text-[#6f8199]">
          Retry in {profile.retry_seconds}s{profile.next_retry_at ? ` · ${new Date(profile.next_retry_at).toLocaleString()}` : ''}
        </div>
      ) : null}
      <details open={profile.status !== 'checked'} className="mt-2 rounded-md border border-[#243447]/70 bg-[#05080d]">
        <summary className="cursor-pointer select-none px-2.5 py-2 font-semibold text-[#8fefff]">
          Full profile diagnostic JSON
        </summary>
        <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words border-t border-[#243447]/70 p-2.5 font-mono text-[11px] leading-5 text-[#b9c4d4]">
          {JSON.stringify(profile, null, 2)}
        </pre>
      </details>
    </div>
  );
};

const SplitPanelDashboard: React.FC = () => {
  const [apiKey, setApiKey] = useState(getStoredApiKey);
  const [adminKey, setAdminKey] = useState(getStoredAdminApiKey);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [selectedInsights, setSelectedInsights] = useState<ProfileInsights | null>(null);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [error, setError] = useState('');
  const [newProfile, setNewProfile] = useState('');
  const [addingProfile, setAddingProfile] = useState(false);
  const [bulkProfileInput, setBulkProfileInput] = useState('');
  const [bulkScrapeResult, setBulkScrapeResult] = useState<ScrapeResponse | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [postUrlInput, setPostUrlInput] = useState('');
  const [postInspection, setPostInspection] = useState<PublicPostInspection | null>(null);
  const [postLoading, setPostLoading] = useState(false);
  const [mediaExtraction, setMediaExtraction] = useState<ProfileMediaExtraction | null>(null);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [hashtagInput, setHashtagInput] = useState('');
  const [hashtagExplorer, setHashtagExplorer] = useState<HashtagExplorer | null>(null);
  const [hashtagLoading, setHashtagLoading] = useState(false);
  const [scraperStatus, setScraperStatus] = useState<CapabilityStatus | null>(null);
  const [maintenanceStatus, setMaintenanceStatus] = useState<AdminDeployStatus | null>(null);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [deployLoading, setDeployLoading] = useState(false);
  const [restartLoading, setRestartLoading] = useState(false);
  const [trackingRefreshStatus, setTrackingRefreshStatus] = useState<AdminTrackingRefreshStatus | null>(null);
  const [trackingRefreshLoading, setTrackingRefreshLoading] = useState(false);
  const [trackingRefreshEventType, setTrackingRefreshEventType] = useState('');
  const [trackingTestPushLoading, setTrackingTestPushLoading] = useState(false);
  const [trackingTestPushResult, setTrackingTestPushResult] = useState<AdminTrackingTestPushResult | null>(null);
  const [profileSearch, setProfileSearch] = useState('');

  const { isConnected } = useWebSocket(WS_URL);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const trimmedKey = apiKey.trim();
    if (trimmedKey) {
      window.localStorage.setItem(API_KEY_STORAGE_KEY, trimmedKey);
    } else {
      window.localStorage.removeItem(API_KEY_STORAGE_KEY);
    }
  }, [apiKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const trimmedKey = adminKey.trim();
    if (trimmedKey) {
      window.localStorage.setItem(ADMIN_API_KEY_STORAGE_KEY, trimmedKey);
    } else {
      window.localStorage.removeItem(ADMIN_API_KEY_STORAGE_KEY);
    }
  }, [adminKey]);

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement, Event>, username: string) => {
    const target = event.target as HTMLImageElement;
    target.src = getAvatarFallbackUrl(username || 'instagram', target.width || 48);
  };

  const loadProfiles = useCallback(async () => {
    setProfilesLoading(true);
    try {
      const data = await fetchJson<Profile[]>(`${API_BASE}/api/profiles/`, {}, apiKey);
      setProfiles(data);
      setSelectedProfile((current) => current ?? data[0] ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load profiles');
    } finally {
      setProfilesLoading(false);
    }
  }, [apiKey]);

  const loadBackendStatus = useCallback(async () => {
    try {
      setScraperStatus(await fetchJson<CapabilityStatus>(`${API_BASE}/api/scraper/status`, {}, apiKey));
    } catch (err) {
      console.error('Could not load scraper status', err);
    }
  }, [apiKey]);

  const loadMaintenanceStatus = useCallback(async () => {
    if (!adminKey.trim()) {
      setMaintenanceMessage('Enter ADMIN_API_KEY to load admin status.');
      return;
    }

    setMaintenanceLoading(true);
    setMaintenanceMessage('');
    try {
      const response = await fetch(`${API_BASE}/api/admin/deploy`, {
        headers: buildAdminHeaders(apiKey, adminKey),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(extractApiErrorMessage(data, `Admin status failed with ${response.status}`));
      }
      setMaintenanceStatus(data as AdminDeployStatus);
    } catch (err) {
      setMaintenanceMessage(err instanceof Error ? err.message : 'Could not load deploy status');
    } finally {
      setMaintenanceLoading(false);
    }
  }, [adminKey, apiKey]);

  const loadTrackingRefreshStatus = useCallback(async () => {
    if (!adminKey.trim()) return;
    try {
      const response = await fetch(`${API_BASE}/api/admin/tracking/refresh`, {
        headers: buildAdminHeaders(apiKey, adminKey),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(extractApiErrorMessage(data, `Tracking refresh status failed with ${response.status}`));
      }
      setTrackingRefreshStatus(data as AdminTrackingRefreshStatus);
    } catch (err) {
      setMaintenanceMessage(err instanceof Error ? err.message : 'Could not load tracking refresh status');
    }
  }, [adminKey, apiKey]);

  const loadAdminStatus = useCallback(async () => {
    await Promise.all([loadMaintenanceStatus(), loadTrackingRefreshStatus()]);
  }, [loadMaintenanceStatus, loadTrackingRefreshStatus]);

  useEffect(() => {
    loadProfiles();
    loadBackendStatus();
  }, [loadProfiles, loadBackendStatus]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      loadBackendStatus();
    }, 5000);
    return () => window.clearInterval(timer);
  }, [loadBackendStatus]);

  useEffect(() => {
    if (!trackingRefreshStatus?.running) return;
    const timer = window.setInterval(loadTrackingRefreshStatus, 1500);
    return () => window.clearInterval(timer);
  }, [loadTrackingRefreshStatus, trackingRefreshStatus?.running]);

  useEffect(() => {
    if (!selectedProfile?.username) {
      setSelectedInsights(null);
      return;
    }

    let cancelled = false;
    const username = selectedProfile.username;

    const loadInsights = async () => {
      setInsightsLoading(true);
      setError('');
      try {
        const data = await fetchJson<ProfileInsights>(`${API_BASE}/api/insights/${encodeURIComponent(username)}`, {}, apiKey);
        if (cancelled) return;
        setSelectedInsights(data);
        setProfiles((rows) => rows.map((profile) => (profile.username === username ? { ...profile, ...data.profile } : profile)));
        setSelectedProfile((current) => (current?.username === username ? { ...current, ...data.profile } : current));
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load insights');
      } finally {
        if (!cancelled) setInsightsLoading(false);
      }
    };

    loadInsights();
    return () => {
      cancelled = true;
    };
  }, [apiKey, selectedProfile?.username]);

  const filteredProfiles = useMemo(() => {
    const term = profileSearch.trim().toLowerCase();
    return profiles
      .filter((profile) => {
        if (!term) return true;
        return `${profile.username} ${profile.profile_name}`.toLowerCase().includes(term);
      })
      .sort((a, b) => b.followers_count - a.followers_count);
  }, [profiles, profileSearch]);

  const chartData = useMemo(
    () =>
      (selectedInsights?.recent_posts || [])
        .slice(0, 8)
        .reverse()
        .map((post) => ({
          name: post.shortcode.slice(0, 6),
          likes: post.likes_count,
          comments: post.comments_count,
        })),
    [selectedInsights]
  );

  const mediaMix = useMemo(
    () => Object.entries(selectedInsights?.analytics.media_mix || {}).sort((a, b) => b[1] - a[1]),
    [selectedInsights]
  );

  const addProfile = async () => {
    const username = safeUsername(newProfile);
    if (!username) return;
    setAddingProfile(true);
    setError('');
    try {
      await fetchJson(`${API_BASE}/api/scraper/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username }),
      }, apiKey);
      setNewProfile('');
      await loadProfiles();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add profile');
    } finally {
      setAddingProfile(false);
    }
  };

  const runBulkScrape = async () => {
    const usernames = bulkProfileInput
      .split(/[\s,]+/)
      .map(safeUsername)
      .filter(Boolean)
      .slice(0, 10);
    if (!usernames.length) return;
    setBulkLoading(true);
    setError('');
    try {
      const data = await fetchJson<ScrapeResponse>(`${API_BASE}/api/scraper/profiles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ usernames }),
      }, apiKey);
      setBulkScrapeResult(data);
      await loadProfiles();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not run batch scrape');
    } finally {
      setBulkLoading(false);
    }
  };

  const inspectPublicPost = async (nextUrl?: string) => {
    const target = (nextUrl ?? postUrlInput).trim();
    if (!target) return;
    setPostLoading(true);
    setError('');
    if (nextUrl) setPostUrlInput(target);
    try {
      const query = new URLSearchParams({ url: target });
      setPostInspection(await fetchJson<PublicPostInspection>(`${API_BASE}/api/insights/post-inspect?${query}`, {}, apiKey));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not inspect post');
    } finally {
      setPostLoading(false);
    }
  };

  const loadProfileMediaAssets = async () => {
    const username = selectedProfile?.username;
    if (!username) return;
    setMediaLoading(true);
    setError('');
    try {
      setMediaExtraction(
        await fetchJson<ProfileMediaExtraction>(
          `${API_BASE}/api/insights/${encodeURIComponent(username)}/media-assets?limit=12`,
          {},
          apiKey
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load media assets');
    } finally {
      setMediaLoading(false);
    }
  };

  const exploreHashtag = async (nextTag?: string) => {
    const tag = (nextTag ?? hashtagInput).trim().replace(/^#/, '');
    if (!tag) return;
    setHashtagLoading(true);
    setError('');
    if (nextTag) setHashtagInput(tag);
    try {
      const query = new URLSearchParams({ tag, limit: '6' });
      setHashtagExplorer(await fetchJson<HashtagExplorer>(`${API_BASE}/api/insights/hashtag-explore?${query}`, {}, apiKey));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not explore hashtag');
    } finally {
      setHashtagLoading(false);
    }
  };

  const deployBackend = async () => {
    if (!adminKey.trim()) {
      setMaintenanceMessage('Enter ADMIN_API_KEY before deploying backend.');
      return;
    }
    if (!window.confirm('Pull latest code, build if needed, and restart backend if code changed?')) return;

    setDeployLoading(true);
    setMaintenanceMessage('Scheduling backend deploy...');
    try {
      const response = await fetch(`${API_BASE}/api/admin/deploy`, {
        method: 'POST',
        headers: buildAdminHeaders(apiKey, adminKey),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(extractApiErrorMessage(data, `Deploy failed with ${response.status}`));
      }
      setMaintenanceMessage(data.message || 'Backend deploy scheduled.');
      await loadMaintenanceStatus();
    } catch (err) {
      setMaintenanceMessage(err instanceof Error ? err.message : 'Backend deploy failed');
    } finally {
      setDeployLoading(false);
    }
  };

  const restartBackend = async () => {
    if (!adminKey.trim()) {
      setMaintenanceMessage('Enter ADMIN_API_KEY before restarting backend.');
      return;
    }
    if (!window.confirm('Restart backend now? The admin panel may be unavailable for a few seconds.')) return;

    setRestartLoading(true);
    setMaintenanceMessage('Scheduling backend restart...');
    try {
      const response = await fetch(`${API_BASE}/api/admin/restart`, {
        method: 'POST',
        headers: buildAdminHeaders(apiKey, adminKey),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(extractApiErrorMessage(data, `Restart failed with ${response.status}`));
      }
      setMaintenanceMessage(data.message || 'Backend restart scheduled.');
      window.setTimeout(() => {
        setRestartLoading(false);
        loadBackendStatus();
        loadMaintenanceStatus();
      }, 8000);
    } catch (err) {
      setMaintenanceMessage(err instanceof Error ? err.message : 'Backend restart failed');
      setRestartLoading(false);
    }
  };

  const refreshAllTrackingChanges = async () => {
    if (!adminKey.trim()) {
      setMaintenanceMessage('Enter ADMIN_API_KEY before refreshing tracking.');
      return;
    }
    if (!window.confirm(
      `Force-scan every active tracked account for “${trackingDebugEventLabel(trackingRefreshEventType)}”? This uses Hiker API requests and may send a real push notification.`,
    )) return;

    setTrackingRefreshLoading(true);
    setMaintenanceMessage('Starting forced tracking refresh...');
    try {
      const response = await fetch(`${API_BASE}/api/admin/tracking/refresh`, {
        method: 'POST',
        headers: buildAdminHeaders(apiKey, adminKey, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ event_type: trackingRefreshEventType || null }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(extractApiErrorMessage(data, `Tracking refresh failed with ${response.status}`));
      }
      setTrackingRefreshStatus(data as AdminTrackingRefreshStatus);
      setMaintenanceMessage(data.message || 'Forced tracking refresh started.');
    } catch (err) {
      setMaintenanceMessage(err instanceof Error ? err.message : 'Could not start tracking refresh');
    } finally {
      setTrackingRefreshLoading(false);
    }
  };

  const sendTrackingTestPush = async () => {
    if (!adminKey.trim()) {
      setMaintenanceMessage('Enter ADMIN_API_KEY before sending a test push.');
      return;
    }
    if (!window.confirm('Send a real APNs diagnostic notification to every active tracking device?')) return;

    setTrackingTestPushLoading(true);
    setTrackingTestPushResult(null);
    setMaintenanceMessage('Sending APNs diagnostic notification...');
    try {
      const response = await fetch(`${API_BASE}/api/admin/tracking/test-push`, {
        method: 'POST',
        headers: buildAdminHeaders(apiKey, adminKey, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ event_type: trackingRefreshEventType || 'following_added' }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(extractApiErrorMessage(data, `Test push failed with ${response.status}`));
      }
      const result = data as AdminTrackingTestPushResult;
      setTrackingTestPushResult(result);
      setMaintenanceMessage(
        result.sent > 0
          ? `APNs accepted ${result.sent} diagnostic push(es).`
          : 'APNs did not accept a diagnostic push. See the exact reason below.',
      );
    } catch (err) {
      setMaintenanceMessage(err instanceof Error ? err.message : 'Could not send APNs diagnostic push');
    } finally {
      setTrackingTestPushLoading(false);
    }
  };

  const downloadFile = async (url: string, fallbackFilename: string) => {
    setError('');
    try {
      const response = await fetch(url, { headers: buildAuthHeaders(apiKey) });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(extractApiErrorMessage(data, `Download failed with ${response.status}`));
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = parseDownloadFilename(response.headers.get('content-disposition'), fallbackFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not download file');
    }
  };

  const exportSelectedProfileReport = () => {
    if (!selectedProfile?.username) return;
    downloadFile(
      `${API_BASE}/api/insights/${encodeURIComponent(selectedProfile.username)}/export.xlsx`,
      `${selectedProfile.username}_insights.xlsx`
    );
  };

  const downloadPostMediaBundle = () => {
    const target = (postInspection?.canonical_url || postUrlInput).trim();
    if (!target) return;
    const query = new URLSearchParams({ url: target });
    downloadFile(`${API_BASE}/api/insights/post-media/export.zip?${query}`, 'post_media.zip');
  };

  const downloadProfileMediaBundle = () => {
    if (!selectedProfile?.username) return;
    downloadFile(
      `${API_BASE}/api/insights/${encodeURIComponent(selectedProfile.username)}/media-assets/export.zip?limit=12`,
      `${selectedProfile.username}_media_assets.zip`
    );
  };

  const selectHashtag = (tag: string) => exploreHashtag(tag);
  const inspectRecentPost = (post: RecentPost) => inspectPublicPost(post.url);

  return (
    <div className="min-h-screen bg-[#0b1017] text-white">
      <div className="border-b border-[#243447]/70 bg-[#111827]/95">
        <div className="mx-auto flex max-w-[1500px] flex-col gap-4 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#00d9ff]/15 text-[#8fefff]">
                <BarChart3 className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Instagram Public Analytics</h1>
                <p className="text-sm text-[#8b9bb3]">Profiles, posts, media exports and hashtag discovery from public surfaces.</p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-[#8b9bb3]">
            <span className="inline-flex min-w-[220px] items-center gap-2 rounded-lg border border-[#243447] bg-[#0f1419] px-3 py-2">
              <KeyRound className="h-3.5 w-3.5 text-[#8fefff]" />
              <input
                type="password"
                value={apiKey}
                onChange={(event) => setApiKey(event.target.value)}
                placeholder="API key"
                autoComplete="off"
                className="min-w-0 flex-1 bg-transparent text-xs text-white outline-none placeholder:text-[#5a6b82]"
              />
            </span>
            <span className="inline-flex min-w-[190px] items-center gap-2 rounded-lg border border-[#243447] bg-[#0f1419] px-3 py-2">
              <ShieldCheck className={`h-3.5 w-3.5 ${proxyHealthClass(scraperStatus?.proxy_rotation)}`} />
              {scraperStatus?.proxy_rotation?.enabled ? (
                <span className="whitespace-nowrap">
                  <span className={proxyHealthClass(scraperStatus.proxy_rotation)}>
                    {proxyLiveCount(scraperStatus.proxy_rotation)}/{scraperStatus.proxy_rotation.configured_count}
                  </span>{' '}
                  proxies live
                  {scraperStatus.proxy_rotation.health_check_enabled ? (
                    <span className="text-[#8b9bb3]">
                      {' '}
                      · checked {proxyCheckedCount(scraperStatus.proxy_rotation)}/{scraperStatus.proxy_rotation.configured_count}
                    </span>
                  ) : null}
                  {scraperStatus.proxy_rotation.cooldown_count ? (
                    <span className="text-[#ffd36b]"> · {scraperStatus.proxy_rotation.cooldown_count} cooling</span>
                  ) : null}
                </span>
              ) : (
                <span>Proxy off</span>
              )}
            </span>
            <span className="inline-flex items-center gap-2 rounded-lg border border-[#243447] bg-[#0f1419] px-3 py-2">
              <Radio className={`h-3.5 w-3.5 ${isConnected ? 'text-[#8fffb1]' : 'text-[#ffd36b]'}`} />
              {isConnected ? 'Realtime connected' : 'Realtime reconnecting'}
            </span>
            <a className="inline-flex items-center gap-2 rounded-lg border border-[#243447] bg-[#0f1419] px-3 py-2 hover:text-white" href="/docs" target="_blank" rel="noreferrer">
              <Server className="h-3.5 w-3.5" />
              Swagger
            </a>
            <button
              onClick={() => {
                loadProfiles();
                loadBackendStatus();
              }}
              className="inline-flex items-center gap-2 rounded-lg border border-[#243447] bg-[#0f1419] px-3 py-2 hover:text-white"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-[1500px] grid-cols-1 gap-4 px-4 py-4 xl:grid-cols-[340px,1fr]">
        <aside className="space-y-4">
          <section className="rounded-xl border border-[#243447]/70 bg-[#111827]/80 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-semibold">Tracked Profiles</h2>
              <span className="text-xs text-[#8b9bb3]">{profiles.length} total</span>
            </div>
            <div className="mb-3 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#5a6b82]" />
                <input
                  value={profileSearch}
                  onChange={(event) => setProfileSearch(event.target.value)}
                  placeholder="Search"
                  className="w-full rounded-lg border border-[#243447] bg-[#0f1419] py-2 pl-9 pr-3 text-sm outline-none focus:border-[#00d9ff]"
                />
              </div>
            </div>
            <div className="max-h-[520px] space-y-2 overflow-y-auto pr-1">
              {profilesLoading ? (
                <div className="flex h-32 items-center justify-center text-[#8b9bb3]">Loading profiles...</div>
              ) : filteredProfiles.length ? (
                filteredProfiles.map((profile) => (
                  <button
                    key={profile.id}
                    onClick={() => setSelectedProfile(profile)}
                    className={`w-full rounded-xl border px-3 py-3 text-left transition ${
                      selectedProfile?.id === profile.id
                        ? 'border-[#00d9ff]/50 bg-[#00d9ff]/10'
                        : 'border-[#243447]/60 bg-[#0f1419]/70 hover:border-[#00d9ff]/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <img
                        src={getProfilePictureUrl(profile.username, profile.profile_pic_url, 44)}
                        alt={profile.username}
                        className="h-11 w-11 rounded-full object-cover"
                        onError={(event) => handleImageError(event, profile.username)}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-semibold">{profile.profile_name || profile.username}</span>
                          {profile.is_verified ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-[#8fefff]" /> : null}
                        </div>
                        <div className="truncate text-xs text-[#8b9bb3]">@{profile.username}</div>
                        <div className="mt-1 text-[11px] text-[#5f7289]">
                          {formatNumber(profile.followers_count)} followers · {formatNumber(profile.posts_count)} posts
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              ) : (
                <div className="rounded-xl border border-dashed border-[#243447]/70 px-4 py-8 text-center text-sm text-[#8b9bb3]">
                  Add a public profile to start.
                </div>
              )}
            </div>
          </section>

          <section className="rounded-xl border border-[#243447]/70 bg-[#111827]/80 p-4">
            <h2 className="mb-3 font-semibold">Add Profile</h2>
            <div className="flex gap-2">
              <input
                value={newProfile}
                onChange={(event) => setNewProfile(event.target.value)}
                onKeyDown={(event) => event.key === 'Enter' && addProfile()}
                placeholder="@username"
                className="min-w-0 flex-1 rounded-lg border border-[#243447] bg-[#0f1419] px-3 py-2 text-sm outline-none focus:border-[#00d9ff]"
              />
              <button
                onClick={addProfile}
                disabled={addingProfile || !newProfile.trim()}
                className="inline-flex items-center justify-center rounded-lg bg-[#00d9ff] px-3 py-2 text-sm font-semibold text-[#06111a] disabled:opacity-50"
              >
                {addingProfile ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Add'}
              </button>
            </div>
          </section>

          <section className="rounded-xl border border-[#243447]/70 bg-[#111827]/80 p-4">
            <h2 className="mb-3 font-semibold">Batch Scrape</h2>
            <textarea
              value={bulkProfileInput}
              onChange={(event) => setBulkProfileInput(event.target.value)}
              placeholder="one username per line"
              rows={4}
              className="w-full resize-none rounded-lg border border-[#243447] bg-[#0f1419] px-3 py-2 text-sm outline-none focus:border-[#00d9ff]"
            />
            <button
              onClick={runBulkScrape}
              disabled={bulkLoading || !bulkProfileInput.trim()}
              className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-sm font-semibold hover:bg-white/15 disabled:opacity-50"
            >
              {bulkLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
              Queue Batch
            </button>
            {bulkScrapeResult ? (
              <div className="mt-3 rounded-lg border border-[#243447]/70 bg-[#0f1419]/70 px-3 py-2 text-xs text-[#b9c4d4]">
                {bulkScrapeResult.job_id
                  ? `Queued job ${bulkScrapeResult.job_id.slice(0, 8)} for background scraping.`
                  : `${bulkScrapeResult.success_count} saved, ${bulkScrapeResult.failed_count} failed.`}
              </div>
            ) : null}
          </section>

          <section className="rounded-xl border border-[#243447]/70 bg-[#111827]/80 p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="font-semibold">Admin Maintenance</h2>
              <span className={`rounded-full px-2 py-1 text-[11px] ${(maintenanceStatus?.running || trackingRefreshStatus?.running) ? 'bg-[#ffd36b]/15 text-[#ffd36b]' : 'bg-[#8fffb1]/10 text-[#8fffb1]'}`}>
                {trackingRefreshStatus?.running
                  ? 'Tracking refresh running'
                  : maintenanceStatus?.running
                    ? `Deploy pid ${maintenanceStatus.active_pid}`
                    : 'Idle'}
              </span>
            </div>
            <div className="space-y-2">
              <span className="flex items-center gap-2 rounded-lg border border-[#243447] bg-[#0f1419] px-3 py-2">
                <KeyRound className="h-3.5 w-3.5 text-[#8fefff]" />
                <input
                  type="password"
                  value={adminKey}
                  onChange={(event) => setAdminKey(event.target.value)}
                  placeholder="ADMIN_API_KEY"
                  autoComplete="off"
                  className="min-w-0 flex-1 bg-transparent text-xs text-white outline-none placeholder:text-[#5a6b82]"
                />
              </span>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={loadAdminStatus}
                  disabled={maintenanceLoading || !adminKey.trim()}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-xs font-semibold hover:bg-white/15 disabled:opacity-50"
                >
                  {maintenanceLoading ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Server className="h-3.5 w-3.5" />}
                  Status
                </button>
                <button
                  onClick={restartBackend}
                  disabled={restartLoading || deployLoading || !adminKey.trim()}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-xs font-semibold hover:bg-white/15 disabled:opacity-50"
                >
                  {restartLoading ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  Restart
                </button>
                <button
                  onClick={deployBackend}
                  disabled={deployLoading || restartLoading || !adminKey.trim()}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#00d9ff]/15 px-3 py-2 text-xs font-semibold text-[#8fefff] hover:bg-[#00d9ff]/25 disabled:opacity-50"
                >
                  {deployLoading ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                  Pull + deploy
                </button>
              </div>
              <label className="block space-y-1">
                <span className="text-[11px] font-medium text-[#8b9bb3]">Expected debug event</span>
                <select
                  value={trackingRefreshEventType}
                  onChange={(event) => setTrackingRefreshEventType(event.target.value)}
                  disabled={trackingRefreshLoading || trackingRefreshStatus?.running}
                  className="w-full rounded-lg border border-[#243447] bg-[#0f1419] px-3 py-2 text-xs text-white outline-none focus:border-[#ff4fb8] disabled:opacity-50"
                >
                  {TRACKING_DEBUG_EVENT_OPTIONS.map((option) => (
                    <option key={option.value || 'all'} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <button
                onClick={refreshAllTrackingChanges}
                disabled={trackingRefreshLoading || trackingRefreshStatus?.running || !adminKey.trim()}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#ff4fb8]/15 px-3 py-2.5 text-xs font-semibold text-[#ff9bd5] hover:bg-[#ff4fb8]/25 disabled:opacity-50"
              >
                {(trackingRefreshLoading || trackingRefreshStatus?.running)
                  ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  : <Activity className="h-3.5 w-3.5" />}
                {trackingRefreshStatus?.running ? 'Refreshing tracking...' : 'Refresh tracking now'}
              </button>
              <button
                onClick={sendTrackingTestPush}
                disabled={trackingTestPushLoading || !adminKey.trim()}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#00d9ff]/15 px-3 py-2.5 text-xs font-semibold text-[#8fefff] hover:bg-[#00d9ff]/25 disabled:opacity-50"
              >
                {trackingTestPushLoading
                  ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  : <Bell className="h-3.5 w-3.5" />}
                Send APNs test push
              </button>
              <p className="text-[11px] leading-4 text-[#6f8199]">
                All changes are selected by default. Choosing a specific event filters every other detected change.
              </p>
            </div>
            {maintenanceMessage ? (
              <div className="mt-3 rounded-lg border border-[#243447]/70 bg-[#0f1419]/70 px-3 py-2 text-xs text-[#b9c4d4]">
                {maintenanceMessage}
              </div>
            ) : null}
            {maintenanceStatus ? (
              <div className="mt-3 space-y-2">
                <div className="text-xs text-[#8b9bb3]">
                  Maintenance {maintenanceStatus.enabled ? 'enabled' : 'disabled'} · {maintenanceStatus.running ? 'running' : 'not running'}
                </div>
                {(maintenanceStatus.output || maintenanceStatus.error_output) ? (
                  <pre className="max-h-48 overflow-auto rounded-lg border border-[#243447]/70 bg-[#05080d] p-3 font-mono text-[11px] leading-5 text-[#b9c4d4]">
                    {[maintenanceStatus.output, maintenanceStatus.error_output].filter(Boolean).join('\n')}
                  </pre>
                ) : null}
                <div className="flex items-center justify-between gap-3 pt-1 text-xs text-[#8b9bb3]">
                  <span>Watchdog log</span>
                  <span>
                    {maintenanceStatus.watchdog_checked_at
                      ? `Last check ${new Date(maintenanceStatus.watchdog_checked_at).toLocaleString()}${maintenanceStatus.watchdog_modified_at
                        ? ` · last event ${new Date(maintenanceStatus.watchdog_modified_at).toLocaleString()}`
                        : ''}`
                      : 'No checks recorded'}
                  </span>
                </div>
                <pre className="max-h-64 min-h-[7rem] overflow-auto whitespace-pre-wrap break-words rounded-lg border border-[#243447]/70 bg-[#05080d] p-3 font-mono text-[11px] leading-5 text-[#b9c4d4]">
                  {maintenanceStatus.watchdog_output || 'No watchdog events have been logged yet.'}
                </pre>
              </div>
            ) : null}
            {trackingRefreshStatus ? (
              <div className="mt-3 rounded-lg border border-[#ff4fb8]/20 bg-[#ff4fb8]/5 p-3 text-xs">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-semibold text-[#ff9bd5]">Tracking debug refresh</span>
                  <span className={trackingRefreshStatus.running ? 'text-[#ffd36b]' : 'text-[#8fffb1]'}>
                    {trackingRefreshStatus.running ? 'Running' : trackingRefreshStatus.error ? 'Failed' : 'Finished'}
                  </span>
                </div>
                <div className="mt-1 text-[#8b9bb3]">
                  Expected: {trackingDebugEventLabel(trackingRefreshStatus.event_type)}
                </div>
                {trackingRefreshStatus.summary ? (
                  <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[#b9c4d4]">
                    <span>Selected: {trackingRefreshStatus.summary.selected_profiles}</span>
                    <span>Checked: {trackingRefreshStatus.summary.checked_profiles}</span>
                    <span>Hiker changes observed: {trackingRefreshStatus.summary.events_observed}</span>
                    <span>Changes saved: {trackingRefreshStatus.summary.events_detected}</span>
                    <span>Other changes ignored: {trackingRefreshStatus.summary.events_filtered}</span>
                    <span>Pushes sent: {trackingRefreshStatus.summary.pushes_sent}</span>
                    <span>Failed scans: {trackingRefreshStatus.summary.failed_profiles}</span>
                    <span>Non-premium: {trackingRefreshStatus.summary.skipped_non_premium}</span>
                    <span>Deferred: {trackingRefreshStatus.summary.pushes_deferred}</span>
                    <span>Duration: {trackingRefreshStatus.summary.duration_seconds}s</span>
                  </div>
                ) : null}
                {trackingRefreshStatus.summary?.profiles?.length ? (
                  <div className="mt-3 space-y-2">
                    {trackingRefreshStatus.summary.profiles.map((profile) => (
                      <TrackingProfileDiagnosticCard key={profile.username} profile={profile} />
                    ))}
                  </div>
                ) : null}
                {trackingRefreshStatus.running ? (
                  <div className="mt-2 text-[#8b9bb3]">Waiting for the current worker cycle if needed, then scanning all profiles.</div>
                ) : null}
                {trackingRefreshStatus.error ? (
                  <div className="mt-2 text-[#ff9ba8]">{trackingRefreshStatus.error}</div>
                ) : null}
                <details className="mt-3 rounded-md border border-[#243447]/70 bg-[#05080d]">
                  <summary className="cursor-pointer select-none px-2.5 py-2 font-semibold text-[#8fefff]">
                    Full refresh response JSON
                  </summary>
                  <pre className="max-h-[32rem] overflow-auto whitespace-pre-wrap break-words border-t border-[#243447]/70 p-2.5 font-mono text-[11px] leading-5 text-[#b9c4d4]">
                    {JSON.stringify(trackingRefreshStatus, null, 2)}
                  </pre>
                </details>
              </div>
            ) : null}
            {trackingTestPushResult ? (
              <div className="mt-3 rounded-lg border border-[#00d9ff]/20 bg-[#00d9ff]/5 p-3 text-xs">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-semibold text-[#8fefff]">APNs diagnostic</span>
                  <span className={trackingTestPushResult.sent > 0 ? 'text-[#8fffb1]' : 'text-[#ff9aa8]'}>
                    {trackingTestPushResult.sent > 0 ? 'Accepted' : 'Failed'}
                  </span>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-1 text-[#b9c4d4]">
                  <span>Registered tokens: {trackingTestPushResult.registered_active_tokens}</span>
                  <span>Alerts enabled: {trackingTestPushResult.notifications_enabled_tokens}</span>
                  <span>Tracking ready: {trackingTestPushResult.tracking_ready_tokens}</span>
                  <span>APNs configured: {trackingTestPushResult.apns_configured ? 'yes' : 'no'}</span>
                  <span>Sent: {trackingTestPushResult.sent}</span>
                  <span>Failed: {trackingTestPushResult.failed}</span>
                </div>
                <div className="mt-2 space-y-1">
                  {trackingTestPushResult.results.map((result) => (
                    <div key={`${result.installation}-${result.username}`} className="text-[#8b9bb3]">
                      @{result.username} · {result.environment} · HTTP {result.status_code} · {result.reason || (result.succeeded ? 'Success' : 'Unknown error')}
                    </div>
                  ))}
                  {!trackingTestPushResult.results.length ? (
                    <div className="text-[#ff9aa8]">
                      {trackingTestPushResult.registered_active_tokens === 0
                        ? 'No active APNs device token is registered on the backend.'
                        : trackingTestPushResult.notifications_enabled_tokens === 0
                          ? 'A token exists, but notifications_enabled is off on the backend. Re-enable the app toggle to sync it.'
                          : 'Notifications are enabled, but no enabled tracked profile is attached to the token.'}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </section>
        </aside>

        <main className="space-y-4">
          {error ? (
            <div className="rounded-xl border border-[#ff6b6b]/40 bg-[#ff6b6b]/10 px-4 py-3 text-sm text-[#ffb3bd]">{error}</div>
          ) : null}

          {selectedProfile ? (
            <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-[#243447]/70 bg-[#111827]/80 p-4">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-4">
                  <img
                    src={getProfilePictureUrl(selectedProfile.username, selectedProfile.profile_pic_url, 72)}
                    alt={selectedProfile.username}
                    className="h-[72px] w-[72px] rounded-2xl object-cover"
                    onError={(event) => handleImageError(event, selectedProfile.username)}
                  />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h2 className="truncate text-2xl font-bold">{selectedProfile.profile_name || selectedProfile.username}</h2>
                      {selectedProfile.is_verified ? <CheckCircle2 className="h-5 w-5 text-[#8fefff]" /> : null}
                    </div>
                    <div className="text-sm text-[#8b9bb3]">@{selectedProfile.username}</div>
                    <p className="mt-2 max-w-3xl text-sm text-[#b9c4d4]">{selectedProfile.bio || 'No bio returned from the public profile response.'}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <a
                    href={`https://www.instagram.com/${selectedProfile.username}/`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-sm hover:bg-white/15"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Instagram
                  </a>
                  <button onClick={exportSelectedProfileReport} className="inline-flex items-center gap-2 rounded-lg bg-[#00d9ff]/15 px-3 py-2 text-sm text-[#8fefff] hover:bg-[#00d9ff]/25">
                    <FileSpreadsheet className="h-4 w-4" />
                    XLSX
                  </button>
                  <button onClick={loadProfileMediaAssets} disabled={mediaLoading} className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-sm hover:bg-white/15 disabled:opacity-50">
                    {mediaLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
                    Media
                  </button>
                </div>
              </div>
            </motion.section>
          ) : null}

          {selectedInsights ? (
            <>
              <section className="grid grid-cols-2 gap-4 xl:grid-cols-4">
                <MetricCard label="Followers" value={formatNumber(selectedInsights.profile.followers_count)} icon={<Users className="h-5 w-5" />} />
                <MetricCard label="Posts" value={formatNumber(selectedInsights.profile.posts_count)} icon={<Layers className="h-5 w-5" />} />
                <MetricCard label="Avg ER" value={`${selectedInsights.analytics.average_engagement_rate.toFixed(2)}%`} detail={`${selectedInsights.analytics.recent_posts_analyzed} posts`} icon={<TrendingUp className="h-5 w-5" />} />
                <MetricCard label="Interactions" value={formatNumber(selectedInsights.analytics.average_interactions)} detail="average per post" icon={<Activity className="h-5 w-5" />} />
              </section>

              <section className="grid grid-cols-1 gap-4 xl:grid-cols-[1.3fr,0.7fr]">
                <div className="rounded-xl border border-[#243447]/70 bg-[#111827]/80 p-4">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="font-semibold">Recent Performance</h3>
                    {insightsLoading ? <RefreshCw className="h-4 w-4 animate-spin text-[#8b9bb3]" /> : null}
                  </div>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#243447" />
                        <XAxis dataKey="name" stroke="#8b9bb3" fontSize={12} />
                        <YAxis stroke="#8b9bb3" fontSize={12} />
                        <Tooltip contentStyle={{ background: '#111827', border: '1px solid #243447', borderRadius: 8 }} />
                        <Bar dataKey="likes" fill="#00d9ff" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="comments" fill="#ff8ac9" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="rounded-xl border border-[#243447]/70 bg-[#111827]/80 p-4">
                  <h3 className="mb-4 font-semibold">Public Signals</h3>
                  <div className="space-y-3">
                    <div className="rounded-lg bg-[#0f1419]/80 p-3">
                      <div className="text-xs text-[#8b9bb3]">Media Mix</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {mediaMix.length ? mediaMix.map(([type, count]) => (
                          <span key={type} className="rounded-full bg-[#00d9ff]/10 px-2 py-1 text-xs text-[#8fefff]">{type}: {count}</span>
                        )) : <span className="text-xs text-[#8b9bb3]">No recent posts</span>}
                      </div>
                    </div>
                    <div className="rounded-lg bg-[#0f1419]/80 p-3">
                      <div className="text-xs text-[#8b9bb3]">Top Hashtags</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {selectedInsights.analytics.top_hashtags.length ? selectedInsights.analytics.top_hashtags.map((tag) => (
                          <button key={tag} onClick={() => selectHashtag(tag)} className="rounded-full bg-white/10 px-2 py-1 text-xs hover:bg-white/15">#{tag}</button>
                        )) : <span className="text-xs text-[#8b9bb3]">No hashtags detected</span>}
                      </div>
                    </div>
                    <div className="rounded-lg bg-[#0f1419]/80 p-3">
                      <div className="text-xs text-[#8b9bb3]">Recommendations</div>
                      <ul className="mt-2 space-y-2 text-sm text-[#b9c4d4]">
                        {(selectedInsights.content_intelligence?.recommendations || ['Refresh this profile again as public data becomes available.']).slice(0, 4).map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              <section className="rounded-xl border border-[#243447]/70 bg-[#111827]/80 p-4">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-semibold">Recent Posts</h3>
                  <span className="text-xs text-[#8b9bb3]">{selectedInsights.recent_posts.length} returned</span>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {selectedInsights.recent_posts.slice(0, 6).map((post) => (
                    <button key={post.id || post.shortcode} onClick={() => inspectRecentPost(post)} className="overflow-hidden rounded-xl border border-[#243447]/70 bg-[#0f1419]/80 text-left hover:border-[#00d9ff]/40">
                      <div className="aspect-video bg-[#0b1017]">
                        {post.thumbnail_url || post.display_url ? (
                          <img src={getMediaUrl(post.thumbnail_url || post.display_url || '')} alt={post.shortcode} className="h-full w-full object-cover" onError={(event) => handleImageError(event, post.shortcode)} />
                        ) : null}
                      </div>
                      <div className="p-3">
                        <div className="mb-2 flex items-center justify-between text-xs text-[#8b9bb3]">
                          <span>{post.media_type}</span>
                          <span>{post.engagement_rate.toFixed(2)}% ER</span>
                        </div>
                        <div className="line-clamp-2 text-sm text-[#dce6f2]">{post.caption || post.shortcode}</div>
                        <div className="mt-3 flex gap-4 text-xs text-[#8b9bb3]">
                          <span className="inline-flex items-center gap-1"><Heart className="h-3.5 w-3.5" />{formatNumber(post.likes_count)}</span>
                          <span className="inline-flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5" />{formatNumber(post.comments_count)}</span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            </>
          ) : (
            <section className="rounded-xl border border-dashed border-[#243447]/70 bg-[#111827]/60 px-4 py-16 text-center text-[#8b9bb3]">
              Select or add a public profile to load analytics.
            </section>
          )}

          <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div className="rounded-xl border border-[#243447]/70 bg-[#111827]/80 p-4">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-semibold">Public Post Inspector</h3>
                <ShieldCheck className="h-4 w-4 text-[#8fffb1]" />
              </div>
              <div className="flex gap-2">
                <input
                  value={postUrlInput}
                  onChange={(event) => setPostUrlInput(event.target.value)}
                  placeholder="Instagram post or reel URL"
                  className="min-w-0 flex-1 rounded-lg border border-[#243447] bg-[#0f1419] px-3 py-2 text-sm outline-none focus:border-[#00d9ff]"
                />
                <button onClick={() => inspectPublicPost()} disabled={postLoading || !postUrlInput.trim()} className="inline-flex items-center gap-2 rounded-lg bg-[#00d9ff] px-3 py-2 text-sm font-semibold text-[#06111a] disabled:opacity-50">
                  {postLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Inspect'}
                </button>
              </div>
              {postInspection ? (
                <div className="mt-4 space-y-4">
                  <div className="flex items-center justify-between gap-3 rounded-xl border border-[#243447]/70 bg-[#0f1419]/80 p-3">
                    <div className="min-w-0">
                      <div className="truncate font-semibold">@{postInspection.owner.username}</div>
                      <div className="text-xs text-[#8b9bb3]">{postInspection.media_type} · {postInspection.shortcode}</div>
                    </div>
                    <div className="flex gap-2">
                      <a href={postInspection.canonical_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-lg bg-white/10 px-2 py-1.5 text-xs hover:bg-white/15">
                        <ArrowUpRight className="h-3.5 w-3.5" />
                        Open
                      </a>
                      <button onClick={downloadPostMediaBundle} className="inline-flex items-center gap-1 rounded-lg bg-[#00d9ff]/15 px-2 py-1.5 text-xs text-[#8fefff] hover:bg-[#00d9ff]/25">
                        <Download className="h-3.5 w-3.5" />
                        ZIP
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <MetricCard label="Likes" value={formatNumber(postInspection.likes_count)} icon={<Heart className="h-4 w-4" />} />
                    <MetricCard label="Comments" value={formatNumber(postInspection.comments_count)} icon={<MessageCircle className="h-4 w-4" />} />
                    <MetricCard label="Views" value={formatNumber(postInspection.views_count)} icon={<Activity className="h-4 w-4" />} />
                  </div>
                  <p className="text-sm text-[#b9c4d4]">{postInspection.caption || postInspection.public_comments_message}</p>
                  {postInspection.assets.length ? (
                    <div className="grid grid-cols-2 gap-3">
                      {postInspection.assets.slice(0, 4).map((asset) => (
                        <a key={`${asset.post_id}-${asset.order_index}-${asset.asset_url}`} href={asset.asset_url} target="_blank" rel="noreferrer" className="overflow-hidden rounded-lg border border-[#243447]/70 bg-[#0f1419]">
                          <div className="aspect-video">
                            <img src={getMediaUrl(asset.preview_url || asset.asset_url)} alt={asset.shortcode} className="h-full w-full object-cover" onError={(event) => handleImageError(event, asset.shortcode)} />
                          </div>
                          <div className="p-2 text-xs text-[#8b9bb3]">{asset.asset_type}</div>
                        </a>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="rounded-xl border border-[#243447]/70 bg-[#111827]/80 p-4">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-semibold">Hashtag Explorer</h3>
                <Hash className="h-4 w-4 text-[#8fefff]" />
              </div>
              <div className="flex gap-2">
                <input
                  value={hashtagInput}
                  onChange={(event) => setHashtagInput(event.target.value)}
                  placeholder="#creator"
                  className="min-w-0 flex-1 rounded-lg border border-[#243447] bg-[#0f1419] px-3 py-2 text-sm outline-none focus:border-[#00d9ff]"
                />
                <button onClick={() => exploreHashtag()} disabled={hashtagLoading || !hashtagInput.trim()} className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-sm font-semibold hover:bg-white/15 disabled:opacity-50">
                  {hashtagLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Explore'}
                </button>
              </div>
              {hashtagExplorer ? (
                <div className="mt-4 space-y-4">
                  <div className="rounded-xl border border-[#243447]/70 bg-[#0f1419]/80 p-3 text-sm text-[#b9c4d4]">
                    {hashtagExplorer.message}
                  </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {hashtagExplorer.items.slice(0, 6).map((item) => (
                      <button key={item.canonical_url} onClick={() => inspectPublicPost(item.canonical_url)} className="rounded-lg border border-[#243447]/70 bg-[#0f1419]/80 p-3 text-left hover:border-[#00d9ff]/40">
                        <div className="text-sm font-semibold">@{item.owner.username}</div>
                        <div className="mt-1 text-xs text-[#8b9bb3]">{formatNumber(item.likes_count)} likes · {formatNumber(item.comments_count)} comments</div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </section>

          {mediaExtraction ? (
            <section className="rounded-xl border border-[#243447]/70 bg-[#111827]/80 p-4">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <h3 className="font-semibold">Profile Media Assets</h3>
                  <p className="text-sm text-[#8b9bb3]">{mediaExtraction.message}</p>
                </div>
                <button onClick={downloadProfileMediaBundle} className="inline-flex items-center gap-2 rounded-lg bg-[#00d9ff]/15 px-3 py-2 text-sm text-[#8fefff] hover:bg-[#00d9ff]/25">
                  <Download className="h-4 w-4" />
                  ZIP
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
                {mediaExtraction.assets.slice(0, 12).map((asset) => (
                  <a key={`${asset.post_id}-${asset.order_index}-${asset.asset_url}`} href={asset.asset_url} target="_blank" rel="noreferrer" className="overflow-hidden rounded-xl border border-[#243447]/70 bg-[#0f1419]/80 hover:border-[#00d9ff]/40">
                    <div className="aspect-video bg-[#0b1017]">
                      <img src={getMediaUrl(asset.preview_url || asset.asset_url)} alt={asset.shortcode} className="h-full w-full object-cover" onError={(event) => handleImageError(event, asset.shortcode)} />
                    </div>
                    <div className="p-3">
                      <div className="text-xs font-semibold uppercase text-white">{asset.asset_type}</div>
                      <div className="mt-1 text-[11px] text-[#8b9bb3]">{asset.shortcode}</div>
                    </div>
                  </a>
                ))}
              </div>
            </section>
          ) : null}

          {scraperStatus ? (
            <section className="rounded-xl border border-[#243447]/70 bg-[#111827]/80 p-4">
              <div className="mb-3 flex items-center gap-2">
                <Server className="h-4 w-4 text-[#8fefff]" />
                <h3 className="font-semibold">Backend Surface</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {scraperStatus.capabilities.map((capability) => (
                  <span key={capability} className="rounded-full bg-[#0f1419] px-3 py-1.5 text-xs text-[#b9c4d4]">
                    {capability}
                  </span>
                ))}
              </div>
            </section>
          ) : null}
        </main>
      </div>
    </div>
  );
};

export default SplitPanelDashboard;
